import type { Locale } from "@porsche-customer/slfinrtl-context-types"
import { PTableHead, PTableHeadRow } from "@porsche-design-system/components-react"
import type { InventoryListSortingKey } from "@slfinrtl/ui-bff-routes"
import { useMemo } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import type { TableSortingButtonProps } from "src/components"
import { Checkbox, TableHeadCellFixed, TableSortingButton } from "src/components"
import { messages } from "./inventoryList.messages"
import { inventoryListColumns } from "./inventoryListColumns.const"

export interface InventoryListTableHeadProps {
	currentVehiclesSelectionState?: boolean | "indeterminate"
	displayLocation: boolean
	getSort?: (key: InventoryListSortingKey) => TableSortingButtonProps | undefined
	isCarSalesLiteEnabled: boolean
	isFirstRegistrationDateAvailable: boolean
	locale?: Locale
	onCurrentVehiclesSelectionToggle?: () => void
}

export const InventoryListTableHead = ({
	currentVehiclesSelectionState,
	displayLocation,
	getSort = () => undefined,
	isCarSalesLiteEnabled,
	isFirstRegistrationDateAvailable,
	locale,
	onCurrentVehiclesSelectionToggle,
}: InventoryListTableHeadProps) => {
	const sortingLocale = locale?.replace("-", "_")
	const { formatMessage } = useIntl()

	return useMemo(
		() => (
			<PTableHead>
				<PTableHeadRow>
					<TableHeadCellFixed $isFixed $width={inventoryListColumns.actionMenu} />
					<TableHeadCellFixed $isFixed $width={inventoryListColumns.checkbox}>
						<Checkbox
							disabledHint={
								isCarSalesLiteEnabled
									? formatMessage(commonMessages.notAvailableInCarsalesLite)
									: undefined
							}
							onChange={onCurrentVehiclesSelectionToggle}
							checked={currentVehiclesSelectionState === true}
							indeterminate={currentVehiclesSelectionState === "indeterminate"}
							compact
						/>
					</TableHeadCellFixed>
					<TableHeadCellFixed $width={inventoryListColumns.modelName}>
						<TableSortingButton {...getSort(`modelInformation.name.${sortingLocale}`)}>
							<FormattedMessage {...messages.headerModelName} />
						</TableSortingButton>
					</TableHeadCellFixed>
					<TableHeadCellFixed $width={inventoryListColumns.inventoryStatus}>
						<FormattedMessage {...messages.headerInventoryStatus} />
						<TableSortingButton {...getSort("arrivalDate")}>
							<FormattedMessage {...messages.headerInventoryDuration} />
						</TableSortingButton>
					</TableHeadCellFixed>
					<TableHeadCellFixed $width={inventoryListColumns.salesStatus}>
						<TableSortingButton {...getSort("salesStatus")}>
							<FormattedMessage {...messages.headerSalesStatus} />
						</TableSortingButton>
					</TableHeadCellFixed>
					<TableHeadCellFixed $width={inventoryListColumns.vehicleType}>
						<FormattedMessage {...messages.headerVehicleType} />
					</TableHeadCellFixed>

					{isFirstRegistrationDateAvailable ? (
						<TableHeadCellFixed $width={inventoryListColumns.mileageFirstRegistrationDate}>
							<TableSortingButton {...getSort("mileage.value")}>
								<FormattedMessage {...messages.headerMileage} />
							</TableSortingButton>
							<TableSortingButton {...getSort("firstRegistrationDate")}>
								<FormattedMessage {...messages.headerFirstRegistrationDate} />
							</TableSortingButton>
						</TableHeadCellFixed>
					) : (
						<TableHeadCellFixed $width={inventoryListColumns.mileageModelYear}>
							<TableSortingButton {...getSort("mileage.value")}>
								<FormattedMessage {...messages.headerMileage} />
							</TableSortingButton>
							<TableSortingButton {...getSort("modelInformation.modelYear")}>
								<FormattedMessage {...messages.headerModelYear} />
							</TableSortingButton>
						</TableHeadCellFixed>
					)}

					<TableHeadCellFixed $width={inventoryListColumns.price}>
						<TableSortingButton {...getSort("price.value")}>
							<FormattedMessage {...messages.headerPrice} />
						</TableSortingButton>
						<TableSortingButton {...getSort("lastPriceChange")}>
							<FormattedMessage {...messages.headerPriceChanged} />
						</TableSortingButton>
					</TableHeadCellFixed>
					<TableHeadCellFixed $width={inventoryListColumns.location}>
						{displayLocation && <FormattedMessage {...messages.headerLocation} />}
						<TableSortingButton {...getSort("inventoryNumber")}>
							<FormattedMessage {...messages.headerNumber} />
						</TableSortingButton>
					</TableHeadCellFixed>
				</PTableHeadRow>
			</PTableHead>
		),
		[
			displayLocation,
			getSort,
			formatMessage,
			isCarSalesLiteEnabled,
			isFirstRegistrationDateAvailable,
			sortingLocale,
			currentVehiclesSelectionState,
			onCurrentVehiclesSelectionToggle,
		],
	)
}
